import {
  EligibilityCriterion,
  MarcaBlancaAuthResponse,
} from "../hooks/useMarcaBlancaAuth";
import { Requirement } from "../components/MarcaBlancaRequirementsModal";

export type RequirementModalConfig = {
  title: string;
  intro: string;
  requirements: Requirement[];
  variant: "requirement" | "generic";
  detail?: string;
  actionLabel: string;
  actionUrl?: string;
};

type Translations = Record<string, string>;

type UrlConfig = {
  epaycoRegisterUrl: string;
  epaycoHomeUrl: string;
  epaycoValidateAccountUrl: string;
  daviviendaOpenAccountUrl: string;
};

const TRACKED_CODES = [
  "NOT_AGGREGATOR",
  "NOT_FULLY_VALIDATED",
  "NO_DAVIVIENDA_ACCOUNT",
] as const;

type TrackedCode = (typeof TRACKED_CODES)[number];

const REQUIREMENT_TEXTS: Record<
  TrackedCode,
  {
    label: string;
    description: string;
    descriptionWithAction?: string;
    actionLabel: string;
    urlKey: keyof UrlConfig;
  }
> = {
  NOT_AGGREGATOR: {
    label: "marcaBlancaReqAggregator",
    description: "marcaBlancaReqAggregatorDesc",
    actionLabel: "marcaBlancaReqCtaCreate",
    urlKey: "epaycoRegisterUrl",
  },
  NOT_FULLY_VALIDATED: {
    label: "marcaBlancaReqValidated",
    description: "marcaBlancaReqValidatedDesc",
    actionLabel: "marcaBlancaReqCtaValidate",
    urlKey: "epaycoValidateAccountUrl",
  },
  NO_DAVIVIENDA_ACCOUNT: {
    label: "marcaBlancaReqDavivienda",
    description: "marcaBlancaReqDaviviendaDesc",
    descriptionWithAction: "marcaBlancaReqDaviviendaDescAction",
    actionLabel: "marcaBlancaReqCtaLink",
    urlKey: "daviviendaOpenAccountUrl",
  },
};

const buildRequirements = (
  criteria: EligibilityCriterion[] | null,
  t: Translations,
  config: UrlConfig
): Requirement[] =>
  TRACKED_CODES.map((code) => {
    const texts = REQUIREMENT_TEXTS[code];
    const met = criteria
      ? criteria.find((item) => item.code === code)?.passed === true
      : false;
    const hasAction = Boolean(criteria) && !met;
    const descriptionKey =
      hasAction && texts.descriptionWithAction
        ? texts.descriptionWithAction
        : texts.description;

    const requirement: Requirement = {
      label: t[texts.label],
      met,
      description: t[descriptionKey],
    };

    if (hasAction) {
      requirement.actionLabel = t[texts.actionLabel];
      requirement.actionUrl = config[texts.urlKey];
    }

    return requirement;
  });

export const getRequirementModalConfig = (
  response: MarcaBlancaAuthResponse | null,
  t: Translations,
  config: UrlConfig
): RequirementModalConfig => {
  const title = t["marcaBlancaReqTitle"];

  if (response?.documentTypeMismatch) {
    return {
      title: t["marcaBlancaGenericErrorTitle"],
      intro: "",
      requirements: [],
      variant: "generic",
      detail: t["marcaBlancaDocumentTypeMismatch"],
      actionLabel: t["marcaBlancaGenericErrorSupport"],
    };
  }

  const criteria = response?.data?.eligibilityCriteria;

  if (!Array.isArray(criteria) || criteria.length === 0) {
    return {
      title,
      intro: t["marcaBlancaReqIntroNotFound"],
      requirements: buildRequirements(null, t, config),
      variant: "requirement",
      actionLabel: t["marcaBlancaReqCtaCreate"],
      actionUrl: config.epaycoRegisterUrl,
    };
  }

  const failedCodes = criteria
    .filter((item) => item.passed === false)
    .map((item) => item.code);

  const hasFailed = (code: string): boolean => failedCodes.indexOf(code) !== -1;

  if (hasFailed("FEATURE_ACTIVE")) {
    return {
      title: t["marcaBlancaFeatureInactiveTitle"],
      intro: "",
      requirements: [],
      variant: "generic",
      detail: t["marcaBlancaFeatureInactiveMessage"],
      actionLabel: t["marcaBlancaGenericErrorSupport"],
    };
  }

  if (hasFailed("HAS_DATAPHONE_MB")) {
    return {
      title,
      intro: t["marcaBlancaReqIntroAlreadyBought"],
      requirements: [],
      variant: "requirement",
      actionLabel: t["marcaBlancaReqAcknowledge"],
    };
  }

  if (TRACKED_CODES.some((code) => hasFailed(code))) {
    return {
      title,
      intro: t["marcaBlancaReqIntro"],
      requirements: buildRequirements(criteria, t, config),
      variant: "requirement",
      actionLabel: t["marcaBlancaReqAcknowledge"],
      actionUrl: config.epaycoHomeUrl,
    };
  }

  return {
    title: t["marcaBlancaGenericErrorTitle"],
    intro: "",
    requirements: [],
    variant: "generic",
    detail: response?.textResponse ?? "",
    actionLabel: t["marcaBlancaGenericErrorSupport"],
  };
};

export const getMissingMarcaBlancaFields = (
  shippingDetails: any,
  commerceId: string | number | null | undefined
): string[] => {
  const requiredFields: Record<string, unknown> = {
    firstName: shippingDetails?.firstName,
    lastName: shippingDetails?.lastName,
    documentId: shippingDetails?.documentId,
    email: shippingDetails?.email,
    phone: shippingDetails?.phone,
    address1: shippingDetails?.address1,
    city: shippingDetails?.city,
    branchId: shippingDetails?.branchId,
    commerceId,
  };

  return Object.keys(requiredFields).filter(
    (field) => String(requiredFields[field] ?? "").trim() === ""
  );
};
